import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { ArrowRight, Cpu, Package, Newspaper } from "lucide-react";
import { useT, useMedia } from "@/lib/i18n";
import heroImg from "@/assets/bosque-hero.jpg";

export const Route = createFileRoute("/services/")({
  component: ServicesIndex,
  head: () => ({
    meta: [
      { title: "Actividades y Tarifas — Bosque Suspendido" },
      { name: "description", content: "Descubre todas las actividades del Bosque Suspendido, las tarifas y los programas para escolares y grupos." },
      { property: "og:title", content: "Actividades y Tarifas — Bosque Suspendido" },
      { property: "og:description", content: "Actividades, tarifas y programas para grupos y colegios." },
      { property: "og:url", content: "https://bosque-suspendido.lovable.app/services" },
    ],
    links: [{ rel: "canonical", href: "https://bosque-suspendido.lovable.app/services" }],
  }),
});

function ServicesIndex() {
  const { tk } = useT();
  const hero = useMedia("services.hero", heroImg, "Actividades Bosque Suspendido");

  const cards = [
    {
      to: "/services/areas" as const,
      icon: Cpu,
      kicker: tk("services.index.areas.kicker", "Actividades"),
      title: tk("services.index.areas.title", "Todas las actividades"),
      desc: tk("services.index.areas.desc", "Tirolinas, circuitos, escalada, paintball, lucha de arqueros y mucho más."),
    },
    {
      to: "/services/paquetes" as const,
      icon: Package,
      kicker: tk("services.index.paquetes.kicker", "Tarifas"),
      title: tk("services.index.paquetes.title", "Precios y paquetes"),
      desc: tk("services.index.paquetes.desc", "Tarifas para particulares, cumpleaños, grupos, empresas y escolares."),
    },
    {
      to: "/services/casos" as const,
      icon: Newspaper,
      kicker: tk("services.index.casos.kicker", "Grupos"),
      title: tk("services.index.casos.title", "Escolares y grupos"),
      desc: tk("services.index.casos.desc", "Programas educativos, despedidas, empresas y celebraciones."),
    },
  ];

  return (
    <Layout>
      <section className="relative border-b border-gold/15 bg-cover bg-center" style={{ backgroundImage: `url(${hero.url})` }}>
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 text-center">
          <span className="text-xs tracking-[0.4em] uppercase text-gold">{tk("services.kicker", "Actividades y tarifas")}</span>
          <h1 className="font-serif text-5xl md:text-7xl mt-5 max-w-4xl mx-auto text-white">
            {tk("services.title.a", "Aventuras para ")}
            <em className="not-italic gold-gradient">{tk("services.title.b", "todas las edades")}</em>
          </h1>
          <p className="mt-8 text-white/80 max-w-2xl mx-auto leading-relaxed">
            {tk("services.intro", "Explora todas las actividades, las tarifas y los programas pensados para grupos y colegios.")}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.to}
                to={c.to}
                className="group rounded-2xl border border-gold/20 bg-card/30 p-8 flex flex-col hover:border-gold/60 hover:bg-card/60 transition-colors"
              >
                <div className="flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-gold/80">
                  <Icon size={16} className="text-gold" />
                  <span>{c.kicker}</span>
                </div>
                <h2 className="font-serif text-2xl md:text-3xl mt-5 group-hover:text-gold transition-colors">{c.title}</h2>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed flex-1">{c.desc}</p>
                <div className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-gold/80 group-hover:text-gold">
                  {tk("services.index.cta", "Learn more")} <ArrowRight size={12} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </Layout>
  );
}
